import React, { useEffect, useState } from 'react';
import { getMonthlyDashboard } from '../api';
import api from '../api';
import { formatCurrency } from '../utils/formatCurrency';

export default function Dashboard({ month, refreshKey }) {

  const [data, setData] = useState({});
  const [contributions, setContributions] = useState({ users: [] });
  const [transfer, setTransfer] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [salaryAvg, setSalaryAvg] = useState([]);
  const [bankBalance, setBankBalance] = useState(null);

  const loadContributions = async () => {
    try {
      const res = await api.get(`/household-contributions/1/${month}`);
      setContributions(res.data);
    } catch (err) {
      console.error("Erreur contributions :", err);
    }
  };

  const loadDashboard = async () => {
    try {
      const [dashRes, transferRes, avgRes, balanceRes] = await Promise.all([
        getMonthlyDashboard(month),
        api.get(`/budget/month/transfer-summary?month=${month}`),
        api.get(`/household/1/income/average?month=${month}&months=12`),
        api.get('/bank/balance').catch(() => ({ data: { balances: [] } })),
      ]);
      setData(dashRes.data);
      setTransfer(transferRes.data);
      setSalaryAvg(avgRes.data || []);
      setBankBalance(balanceRes.data.balances?.[0] || null);
      await loadContributions();
    } catch (err) {
      console.error("Erreur dashboard :", err);
    }
  };

  useEffect(() => {
    if (!month) return;
    loadDashboard();
  }, [month, refreshKey]);

  if (!data || !contributions) {
    return <p style={{ color: "var(--text-soft)" }}>Chargement...</p>;
  }

  const income  = Number(data.income || 0);
  const expense = Math.abs(Number(data.expense || 0));
  const balance = Number(data.balance || 0);

  return (
    <div>

      {/* KPI */}
      <div className="kpi-grid">

        <div className="kpi-card">
          <div className="kpi-label">Revenus</div>
          <div className="kpi-value kpi-success">{formatCurrency(income)}</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Dépenses</div>
          <div className="kpi-value kpi-danger">{formatCurrency(expense)}</div>
        </div>

        <div className="kpi-card" style={{ borderLeft: `4px solid ${balance >= 0 ? "var(--success)" : "var(--danger)"}` }}>
          <div className="kpi-label">Solde</div>
          <div className={`kpi-value ${balance >= 0 ? "kpi-success" : "kpi-danger"}`}>
            {formatCurrency(balance)}
          </div>
        </div>

        {bankBalance && (
          <div className="kpi-card" style={{ borderLeft: '4px solid #2563eb' }}>
            <div className="kpi-label">Compte BoursoBank</div>
            <div className="kpi-value" style={{ color: bankBalance.clbd?.amount >= 0 ? 'var(--text)' : 'var(--danger)' }}>
              {bankBalance.clbd?.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
            {bankBalance.xpcd && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Mvts à venir : {bankBalance.xpcd.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
              </div>
            )}
          </div>
        )}

        {transfer && transfer.total > 0 && (
          <div
            className="kpi-card"
            onClick={() => setTransferOpen(o => !o)}
            style={{ borderLeft: '4px solid #7c3aed', cursor: 'pointer', userSelect: 'none' }}
          >
            <div className="kpi-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>À virer en épargne</span>
              <span style={{ fontSize: 11, color: '#7c3aed' }}>{transferOpen ? '▲' : '▼'}</span>
            </div>
            <div className="kpi-value" style={{ color: '#7c3aed' }}>{formatCurrency(transfer.total)}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, display: 'flex', gap: 10 }}>
              {transfer.provision > 0 && <span>Provisions {formatCurrency(transfer.provision)}</span>}
              {transfer.saving > 0 && <span>Épargne {formatCurrency(transfer.saving)}</span>}
            </div>
          </div>
        )}

      </div>

      {/* DÉTAIL À VIRER */}
      {transfer && transfer.total > 0 && transferOpen && (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid #d8b4fe',
          borderRadius: 10,
          padding: '16px 20px',
          marginBottom: 20,
        }}>
          <div style={{ fontWeight: 600, color: '#7c3aed', marginBottom: 12, fontSize: 14 }}>
            Détail — À virer en épargne
          </div>
          {['provision', 'saving'].map(method => {
            const lines = transfer.lines.filter(l => l.budget_method === method);
            if (!lines.length) return null;
            return (
              <div key={method} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                  {method === 'provision' ? 'Provisions' : 'Épargne'}
                </div>
                {lines.map(l => (
                  <div key={l.subcategory_name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 13, color: 'var(--text)' }}>{l.subcategory_name}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#7c3aed' }}>{formatCurrency(l.planned_amount)}</span>
                  </div>
                ))}
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, fontWeight: 700, fontSize: 14 }}>
            <span>Total</span>
            <span style={{ color: '#7c3aed' }}>{formatCurrency(transfer.total)}</span>
          </div>
        </div>
      )}

      {/* CONTRIBUTIONS */}
      {contributions.users.length > 0 && (
        <div className="contribution-section">

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
            <div>
              <h3 style={{ margin: 0 }}>Répartition du budget</h3>
              {contributions.templateName && (
                <span style={{ fontSize: 12, color: "var(--primary)", fontWeight: 600, marginTop: 3, display: "inline-block" }}>
                  📋 {contributions.templateName}
                </span>
              )}
            </div>
            <span style={{ fontSize: 13, color: "var(--text-soft)" }}>
              Total à financer : <strong style={{ color: "var(--text)" }}>{formatCurrency(contributions.totalToFund || 0)}</strong>
            </span>
          </div>

          <div className="contribution-grid">
            {contributions.users.map(user => (
              <div key={user.userId} className="contribution-card">
                <div className="contribution-name">{user.name}</div>
                <div className="contribution-amount">{formatCurrency(user.contribution)}</div>
                <div className="contribution-ratio">{(user.ratio * 100).toFixed(0)} % des revenus</div>
              </div>
            ))}
          </div>

        </div>
      )}

      {/* REVENUS MOYENS */}
      {salaryAvg.length > 0 && (
        <div className="section-card">
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: 'var(--text)' }}>
            Revenus de référence <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>(moyenne 12 mois)</span>
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {salaryAvg.map(u => (
              <div key={u.id} style={{
                background: 'var(--accent-dim, #dbeafe)',
                border: '1px solid #93c5fd',
                borderRadius: 10,
                padding: '10px 16px',
                minWidth: 160,
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>
                  {u.first_name}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#1d4ed8' }}>
                  {u.average != null ? formatCurrency(u.average) : '—'}
                </div>
                <div style={{ fontSize: 10, color: '#3b82f6', marginTop: 2 }}>
                  {u.contribution_ratio != null ? `${(u.contribution_ratio * 100).toFixed(1)} % du foyer` : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
